import { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStartSettingsCategoryView } from "@/hooks/useStartSettingsCategoryView";
import LoadingScreen from "@/components/shared/LoadingScreen";
import {
  IMPLEMENTED_SETTINGS_CATEGORIES,
  SettingsCategory,
} from "@/zustand/NavigationState";

/**
 * Deep-link target for `/settings/:category` (flag `settingsMiddleViews` —
 * with the flag off the route itself renders a redirect in App.tsx).
 *
 * Opens the settings category middle-column view via
 * useStartSettingsCategoryView(), which clears active chat/thread state and
 * navigates to `/`. Validated against IMPLEMENTED_SETTINGS_CATEGORIES — an
 * unknown or unimplemented category redirects home instead of persisting an
 * empty pane.
 */
export default function SettingsCategoryPage() {
  const { t } = useTranslation();
  const { category } = useParams<{ category?: string }>();
  const startSettingsCategoryView = useStartSettingsCategoryView();

  const validCategory = IMPLEMENTED_SETTINGS_CATEGORIES.includes(
    category as SettingsCategory,
  )
    ? (category as SettingsCategory)
    : null;

  useEffect(() => {
    if (!validCategory) {
      return;
    }
    // `replace: true` so Back from / doesn't bounce the user back to
    // /settings/:category (which would immediately redirect again).
    startSettingsCategoryView(validCategory, { replace: true });
  }, [validCategory, startSettingsCategoryView]);

  if (!validCategory) {
    return <Navigate to="/" replace />;
  }

  return <LoadingScreen title={t("loading")} />;
}
